import React, { useMemo } from "react";
import {
  InspectionType,
  VehicleType,
  VehicleTypeMap,
  VehicleColorMap,
} from "../../utils/enums/index";
import { formatPhoneNumber } from "../../utils/phoneNumber";
import { formatDate } from "../../utils/date";
import AutoFitText from "../AutoFitText";

const PlaceInfo = ({
  placeType,
  inspectionData,
  placeInspectionData,
  contactSignature,
  inspectionType,
  isHistorical,
}) => {
  const isDropOffPending = inspectionData.isDropOffPending;

  // On a historical BOL, hide the signature/printed-name fields for the block
  // that is the opposite of the historical BOL's inspection type, since we don't
  // have anything to display there.
  const hideSignatureFields = isHistorical && placeType !== inspectionType;

  const { title, addressName, address, contactName, contactPhoneNumber } =
    useMemo(() => {
      return {
        title: placeType === InspectionType.Pickup ? "Origin" : "Destination",
        addressName:
          placeType === InspectionType.Pickup
            ? inspectionData.pickupAddressName
            : inspectionData.dropOffAddressName,
        address:
          placeType === InspectionType.Pickup
            ? inspectionData.pickupAddress
            : inspectionData.dropOffAddress,
        contactName:
          placeType === InspectionType.Pickup
            ? inspectionData.pickupContactName
            : inspectionData.dropOffContactName,
        contactPhoneNumber: formatPhoneNumber(
          placeType === InspectionType.Pickup
            ? inspectionData.pickupContactPhoneNumber
            : inspectionData.dropOffContactPhoneNumber,
        ),
      };
    }, [inspectionData, placeType]);

  const contactPrintedName = useMemo(() => {
    if (placeInspectionData?.receiverName)
      return placeInspectionData?.receiverName;
    return null;
  }, [placeInspectionData?.receiverName]);

  const contactSignatureHeader = useMemo(() => {
    if (placeInspectionData?.completedAt && placeInspectionData?.isSigned) {
      return `Contact Signature, ${formatDate(
        placeInspectionData.completedAt,
        true,
      )}`;
    }
    return "Contact Signature";
  }, [placeInspectionData?.completedAt, placeInspectionData?.isSigned]);

  const driverSignatureHeader = useMemo(() => {
    if (
      !isDropOffPending &&
      placeInspectionData?.completedAt &&
      placeInspectionData?.driverSignatureUrl
    ) {
      return `Driver Signature, ${formatDate(
        placeInspectionData.completedAt,
        true,
      )}`;
    }
    return "Driver Signature";
  }, [
    isDropOffPending,
    placeInspectionData?.completedAt,
    placeInspectionData?.driverSignatureUrl,
  ]);

  const driverSignatureContent = useMemo(() => {
    if (isDropOffPending) {
      return <div className="info-box">Pending</div>;
    }
    if (placeInspectionData?.driverSignatureUrl) {
      return (
        <img
          alt="driver-signature"
          src={placeInspectionData.driverSignatureUrl}
        />
      );
    }
    return <div className="info-box">No signature on file</div>;
  }, [isDropOffPending, placeInspectionData?.driverSignatureUrl]);

  return (
    <div className="info-grid-item section-container">
      <div className="section-header">{title}</div>

      <div className="info-box margin-bottom">
        <div>{addressName}</div>
        <div>{address}</div>
      </div>

      <div className="info-box">
        <div>
          <span className="info-box__label-text">Contact: </span>
          <span>{contactName}</span>
        </div>
        <div>
          <span className="info-box__label-text">Phone: </span>
          <span>{contactPhoneNumber}</span>
        </div>
      </div>

      {!hideSignatureFields && (
        <>
          <div className="signature-header">Contact Printed Name</div>
          {contactPrintedName ? (
            <div className="info-box">{contactPrintedName}</div>
          ) : (
            <div className="signature-container" />
          )}

          <div className="signature-header">{contactSignatureHeader}</div>
          {isDropOffPending ? (
            <div className="info-box">Pending</div>
          ) : contactSignature ? (
            <div className="signature-container">
              <img alt="contact-signature" src={contactSignature} />
            </div>
          ) : (
            <div className="info-box">
              {placeType === InspectionType.Pickup
                ? "Picked up"
                : "Dropped off"}{" "}
              without signature
            </div>
          )}

          <div className="signature-header">{driverSignatureHeader}</div>
          <div className="signature-container">{driverSignatureContent}</div>
        </>
      )}
    </div>
  );
};

const InfoGrid = ({
  inspectionData,
  placeInspectionData,
  contactSignatures,
  inspectionType,
  isHistorical,
}) => {
  return (
    <div className="info-grid">
      <div className="info-grid-item section-container">
        <div className="section-header">Vehicle</div>

        {/* <!-- year, make, model, vin, type, color, odometer reading--> */}
        <div className="info-table row">
          {/* <!-- labels --> */}
          <div className="info-table__col col">
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Year</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Make</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Model</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Type</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Color</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>VIN #</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Odometer</AutoFitText>
            </div>
          </div>

          {/* <!-- data --> */}
          <div className="info-table__col col">
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.vehicleYear}</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.vehicleMake}</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.vehicleModel}</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>
                {VehicleTypeMap[inspectionData.vehicleType] ??
                  VehicleTypeMap[VehicleType.Other]}
              </AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>
                {inspectionData?.vehicleOtherColor ??
                  VehicleColorMap[inspectionData.vehicleColor]}
              </AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.vehicleVIN}</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.odometer}</AutoFitText>
            </div>
          </div>
        </div>
      </div>

      <div className="info-grid-item section-container">
        <div className="section-header">Transporter</div>

        {/* <!-- name, phone, company, compnay phone, us dot#, signature--> */}
        <div className="info-table row">
          {/* <!-- labels --> */}
          <div className="info-table__col col">
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Company Name</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Company Phone</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Driver Name</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>Driver Phone</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--label">
              <AutoFitText>US DOT #</AutoFitText>
            </div>
          </div>

          {/* <!-- data --> */}
          <div className="info-table__col col">
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.transporterName}</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>
                {formatPhoneNumber(inspectionData.transporterPhoneNumber)}
              </AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.driverName}</AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>
                {formatPhoneNumber(inspectionData.driverPhoneNumber)}
              </AutoFitText>
            </div>
            <div className="info-table__item info-table__item--data">
              <AutoFitText>{inspectionData.transporterDotNumber}</AutoFitText>
            </div>
          </div>
        </div>
      </div>

      <PlaceInfo
        placeType={InspectionType.Pickup}
        inspectionData={inspectionData}
        placeInspectionData={placeInspectionData[InspectionType.Pickup]}
        contactSignature={contactSignatures[InspectionType.Pickup]}
        inspectionType={inspectionType}
        isHistorical={isHistorical}
      />

      <PlaceInfo
        placeType={InspectionType.DropOff}
        inspectionData={inspectionData}
        placeInspectionData={placeInspectionData[InspectionType.DropOff]}
        contactSignature={contactSignatures[InspectionType.DropOff]}
        inspectionType={inspectionType}
        isHistorical={isHistorical}
      />
    </div>
  );
};

export default InfoGrid;
